import React, {useCallback, useEffect, useRef, useState} from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import {InfoWindow, Marker, GoogleMap, useJsApiLoader} from "@react-google-maps/api";
import {useFilters} from "../helpers/useFilters";
import {useLocation, useNavigate} from "../helpers/routerCompat";
import {useAggsState} from "../../contexts/AggsContext";
import {useLoadAssetFromGuid} from "../widgets/AssetLink";
import {useCurrentAssetsState} from "../../contexts/CurrentAssetContext";
import {LoadingOutlined} from "@ant-design/icons";
import {useSelectedAssetsState} from "../../contexts/SelectedAssetsContext";
import {useTranslation} from "react-i18next";
import {useViewMode} from "./ViewMenu";
import {mapBoundsLiteral, mapBoundsFilter, parseMapBounds} from "./mapBounds";
import AssetDensityLayer from './AssetDensityLayer';
import './AssetsMap.css';

const libraries = ['places'];
const emptyPoints = [];

export default ({})=> {
    const {t} = useTranslation();
    const {coords, mapData} = useAggsState()
    const location = useLocation()
    const navigate = useNavigate()
    const display = new URLSearchParams(location.search).get('map_display') === 'density' ? 'density' : 'clusters'
    const setDisplay = value => {
        const search = new URLSearchParams(location.search)
        search.set('map_display', value)
        navigate({pathname: location.pathname, search: `?${search}`, hash: location.hash}, {replace: true, state: location.state})
    }

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: Config.googleApiKey,
        libraries
    })

    const [map, setMap] = useState(null)
    const [densityStatus, setDensityStatus] = useState('loading')
    const onDensityStatus = useCallback(status => setDensityStatus(status), [])
    const density = mapData?.density
    const densityPoints = density?.points || emptyPoints
    // Google Maps and deck.gl use Web Mercator; retain geohash clusters for
    // data beyond its latitude range instead of silently dropping those assets.
    const densityEligible = !!density && densityPoints.length > 0 &&
        densityPoints.every(point => Math.abs(point.latitude) <= 85.05113)
    const useDensity = display === 'density' && densityEligible && densityStatus === 'ready'
    const initialized = useRef(false)
    const lastBounds = useRef()
    const searchListeners = useRef([])
    const restoreZoomListener = useRef()

    const {zoomAsset} = useCurrentAssetsState()

    useEffect(()=>{
        if(!zoomAsset || !map) return
        console.log('zoomAsset', zoomAsset)

        const timer = setTimeout(()=>{
            map.setZoom(17);
            map.panTo(new window.google.maps.LatLng(zoomAsset.latitude, zoomAsset.longitude));
        }, 1000)
        return () => clearTimeout(timer)
    }, [map, zoomAsset?.id])

    const onLoad = useCallback(function callback(map) {
        setMap(map)

        const input = document.getElementById("pac-input");
        input.style.display = 'block'
        const searchBox = new google.maps.places.SearchBox(input);
        map.controls[google.maps.ControlPosition.TOP_LEFT].push(input)

        const boundsListener = map.addListener("bounds_changed", () => {
            searchBox.setBounds(map.getBounds());
        });

        const placesListener = searchBox.addListener('places_changed', function() {
            const places = searchBox.getPlaces();

            if (places.length == 0) {
                return;
            }

            // For each place, get the icon, name and location.
            const bounds = new google.maps.LatLngBounds();
            places.forEach(function(place) {
                if (!place.geometry) {
                    console.log("Returned place contains no geometry");
                    return;
                }

                if (place.geometry.viewport) {
                    // Only geocodes have viewport.
                    bounds.union(place.geometry.viewport);
                } else {
                    bounds.extend(place.geometry.location);
                }
            });
            map.fitBounds(bounds);
        });
        searchListeners.current = [boundsListener, placesListener]
    }, [])

    const onUnmount = useCallback(function callback(map) {
        searchListeners.current.forEach(listener => listener.remove())
        restoreZoomListener.current?.remove()
        restoreZoomListener.current = null
        initialized.current = false
        setMap(null)
    }, [])

    const toolbarHeight = (document.getElementById('browse-grid-header')?.clientHeight || 0) + 32

    const {setFiltersKeepSelection, filters} = useFilters()

    useEffect(()=>{
        if(!map || initialized.current || !mapData) return;
        initialized.current = true
        const saved = parseMapBounds(filters.bounds)
        if(saved) {
            if(saved.zoom !== undefined) {
                // fitBounds can animate asynchronously and override an immediate
                // setZoom. Restore the saved zoom after that fit completes.
                restoreZoomListener.current = map.addListener('idle', () => {
                    restoreZoomListener.current.remove()
                    restoreZoomListener.current = null
                    map.setZoom(saved.zoom)
                })
            }
            map.fitBounds(saved.bounds)
        } else if(mapData.bounds) {
            map.fitBounds(mapBoundsLiteral(mapData.bounds))
        } else {
            map.setCenter({lat: 41.850033, lng: -87.6500523})
            map.setZoom(3)
        }
    }, [map, mapData, filters.bounds])

    const loadMore = () => {
        if(!map || !initialized.current || restoreZoomListener.current || !map.getBounds()) return
        const bounds = mapBoundsFilter(map.getBounds().toJSON(), map.getZoom())
        if(bounds === lastBounds.current) return
        lastBounds.current = bounds
        if(bounds !== filters.bounds) setFiltersKeepSelection(true, {bounds})
    }

    const searchRef = useRef()

    return (
        <AutoSizer>
            {({height, width}) => (
                <div className="asset-map" style={{width, height: height - toolbarHeight}}>
                    {isLoaded ? (
                        <>
                            <input
                                id="pac-input"
                                className="controls"
                                type="text"
                                placeholder={t('placeholder-search-map',"Search Map...")}
                                ref={searchRef}
                                style={{bottom:-100, right:-100, position: 'fixed'}}
                            />

                            <GoogleMap
                                mapContainerStyle={{width, height: height - toolbarHeight}}
                                // zoom={1}
                                onLoad={onLoad}
                                onUnmount={onUnmount}
                                onIdle={loadMore}
                            >
                                {coords?.map(c => <AssetMarker coord={c} key={c.id} map={map}/>)}
                                {!useDensity && mapData?.clusters?.map(cluster => <ClusterMarker cluster={cluster} key={cluster.key} map={map}/>)}
                            </GoogleMap>
                            {map && display === 'density' && densityStatus !== 'unavailable' &&
                                <AssetDensityLayer map={map} points={densityEligible ? densityPoints : emptyPoints} onStatus={onDensityStatus}/>}
                            <div className="asset-map-display" aria-label={t('map-display', 'Map display')}>
                                <div className="asset-map-display-options">
                                    <button type="button" aria-pressed={display === 'clusters'} onClick={() => setDisplay('clusters')}>
                                        {t('map-clusters', 'Clusters')}
                                    </button>
                                    <button type="button" aria-pressed={display === 'density'} onClick={() => {
                                        if(display !== 'density' || densityStatus === 'unavailable') setDensityStatus('loading')
                                        setDisplay('density')
                                    }}>
                                        {t('map-density', 'Density')}
                                    </button>
                                </div>
                                <div role="status">
                                    {useDensity ? <>
                                        {t('map-concentration', 'Asset concentration')}
                                        <div className="asset-map-density-scale"/>
                                        <div className="asset-map-density-labels"><span>{t('map-density-low', 'Low')}</span><span>{t('map-density-high', 'High')}</span></div>
                                        {t('map-density-hint', 'Zoom in to explore assets. Dots mark sparse locations.')}
                                    </> : display === 'density' && densityStatus === 'unavailable' ?
                                        t('map-density-unavailable', 'Density is unavailable on this device. Showing markers.') :
                                        coords?.length ? t('map-individual-assets', 'Individual assets') :
                                        t('map-counted-clusters', 'Counted clusters · Click to zoom in')}
                                </div>
                            </div>
                        </>
                    ) : (
                        <><LoadingOutlined/> Loading Map...</>
                    )}
                </div>
            )}
        </AutoSizer>
    );
}

const AssetMarker = ({coord, map})=> {
    const loadAssetFromGuid = useLoadAssetFromGuid()

    const {id, latitude, longitude} = coord

    const {cursorAssetId} = useCurrentAssetsState()
    const isCursor = cursorAssetId == id

    const {selectedAssetIds} = useSelectedAssetsState()
    const selected = selectedAssetIds.indexOf(id) != -1

    const onClick = ()=>{
        loadAssetFromGuid(id)
    }

    let url
    if(isCursor) url = 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
    else {
        url = `https://maps.google.com/mapfiles/ms/icons/${selected ? 'blue' : 'red'}-dot.png`
    }

    const [hoverMarker, setHoverMarker] = useState()
    const [asset, setAsset] = useState()

    const onMouseOver = ()=>{
        setHoverMarker(true)
        if(asset) return

        api(`/api/assets/${id}/thumb`).then(res => {
            setAsset(res.data)
        })
    }

    const onClose = ()=>{
        setHoverMarker(null)
    }

    const position = { lat: parseFloat(latitude), lng: parseFloat(longitude) }

    return (
        <>
            <Marker
                key={id}
                position={position}
                onClick={onClick}
                onMouseOver={onMouseOver}
                onMouseOut={onClose}
                icon={{
                    url,
                    // origin: new google.maps.Point(0, 0),
                    // size: new google.maps.Size(20, 32),
                }}
            />

            {hoverMarker && (
                <InfoWindow
                    marker={id}
                    visible
                    onClose={onClose}
                    position={position}
                    options={{
                        pixelOffset: new window.google.maps.Size(0, -30)
                    }}
                >
                    <div>
                        {!asset && <LoadingOutlined style={{margin:'2em'}}/>}
                        {asset && (
                            <>
                                <img src={asset.thumb_url}/>
                            </>
                        )}
                    </div>
                </InfoWindow>
            )}
        </>
    )
}

const ClusterMarker = ({cluster, map}) => {
    const {t} = useTranslation()
    const {setFiltersKeepSelection} = useFilters()
    const {changeViewMode} = useViewMode()
    const [showDetails, setShowDetails] = useState(false)
    const zoomListener = useRef()
    useEffect(() => () => zoomListener.current?.remove(), [])
    const position = {lat: cluster.latitude, lng: cluster.longitude}
    const bounds = mapBoundsLiteral(cluster.bounds)
    const title = t('map-cluster-assets', '{{count}} assets', {count: cluster.count})
    const label = cluster.count.toLocaleString()

    const onClick = () => {
        if(!map) return
        if(map.getZoom() >= 20 || (bounds.north === bounds.south && bounds.east === bounds.west)) {
            setShowDetails(true)
        } else {
            const previousZoom = map.getZoom()
            zoomListener.current?.remove()
            zoomListener.current = map.addListener('idle', () => {
                zoomListener.current.remove()
                // A cluster can already fill the viewport. Still let the user
                // drill down when fitting its extent alone would change nothing.
                if(map.getZoom() <= previousZoom) map.setZoom(Math.min(20, previousZoom + 2))
            })
            map.fitBounds(bounds)
        }
    }

    const viewAssets = () => {
        setFiltersKeepSelection(true, {bounds: mapBoundsFilter(bounds, map.getZoom())})
        changeViewMode('grid')
    }

    return <>
        <Marker position={position} onClick={onClick} title={title}
                label={{text: label, color: '#fff', fontWeight: '600'}}
                icon={{path: window.google.maps.SymbolPath.CIRCLE, scale: Math.max(25, label.length * 5),
                    fillColor: '#245b9e', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2}}/>
        {showDetails && <InfoWindow position={position} onCloseClick={() => setShowDetails(false)}>
            <div><p>{title}</p><button type="button" onClick={viewAssets}>
                {t('map-view-assets', 'View assets')}
            </button></div>
        </InfoWindow>}
    </>
}
